import { LabelType } from '@/enums/LabelType'
import { JaarPeriode, OefenenPeriode, ToetsenPeriode } from '@/enums/Timespan'
import { Yeargroup } from '@/enums/Yeargroup'
import { IExternalProviderUrl } from '@/interfaces/IExternalProvider'
import { ILabel } from '@/interfaces/IPlanning'
import { IResult } from '@/interfaces/IResult'
import { labelsService } from '@/services/labelsService'
import { externalProviderUrlService } from '@/services/resultaten/externalProviderUrlService'
import { methodeToetsResultService } from '@/services/resultaten/methodeToetsResultService'
import { oefeningenResultService } from '@/services/resultaten/oefeningenResultService'
import { summarizeToNotitieService } from '@/services/resultaten/summarizeToNotitieService'
import { notify } from '@kyvg/vue3-notification'
import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { MomentoScore } from './../../enums/MomentoResult'
import { ResultType } from './../../enums/ResultType'
import { IResultFilters } from './../../interfaces/IFilters'
import { IOefeningenResultCollection, IOefeningenResultCollectionResponse, IResultCollectionResponseLike, IToetsenResultCollection, IToetsenResultCollectionResponse } from './../../interfaces/IResult'

const preferredOrder: MomentoScore[] = [MomentoScore.Red, MomentoScore.Orange, MomentoScore.Green, MomentoScore.Unknown]

const showVueNotifyError = (resultType: ResultType, message: string) => {
    notify({
        group: 'requests',
        title: 'Foutmelding',
        type: 'error',
        text: `Er ging iets mis met het ophalen van de resultaten voor ${resultType}, Error: ${message}`,
    })
}

class ResultsState {
    results: IResult[] = []
    resultsOefeningen: IOefeningenResultCollection = {
        moeilijskteOefeningen: [],
        oefeningenResultaat: {
            categorieen: [],
            leerlingen: [],
        },
    }
    resultsToetsen: IToetsenResultCollection = {
        categorieen: [],
        leerlingen: [],
    }
    categories: ILabel[] = []
    filters: IResultFilters = {
        YeargroupFilter: Yeargroup.ALLE,
        TimespanFilter: OefenenPeriode.THIS_WEEK,
        ToetsTimespanFilter: ToetsenPeriode.THIS_WEEK,
        VolgenTimespanFilter: JaarPeriode.THIS_SCHOOLYEAR,
        SociaalTimespanFilter: JaarPeriode.THIS_SCHOOLYEAR,
        CompetentiesTimespanFilter: JaarPeriode.THIS_SCHOOLYEAR,
    }
    loading = false
    oefenenLoading = false
    toetsenLoading = false
    volgToetsenLoading = false
    sociaalEmotioneelLoading = false
    competentiesLoading = false
    externalProviderUrls: IExternalProviderUrl[] = []
    externalProviderLoading = false
    summarizedNoteContent: string | null = null
    summarizeLoading = false
}

const getters: GetterTree<ResultsState, any> = {
    getTimespanFilter(store) {
        return () => store.filters.TimespanFilter
    },
    getToetsTimespanFilter(store) {
        return () => store.filters.ToetsTimespanFilter
    },
    getVolgenTimespanFilter(store) {
        return () => store.filters.VolgenTimespanFilter
    },
    getYeargroupFilter(store) {
        return () => store.filters.YeargroupFilter
    },
    getResults(store) {
        return store.results
    },
    getResultsOefeningen(store) {
        const oefeningen = store.resultsOefeningen
        oefeningen.oefeningenResultaat.categorieen.sort((a, b) => {
            return preferredOrder.indexOf(a.momentoResultaat) - preferredOrder.indexOf(b.momentoResultaat)
        })
        oefeningen.oefeningenResultaat.leerlingen.sort((a, b) => {
            return preferredOrder.indexOf(a.momentoResultaat) - preferredOrder.indexOf(b.momentoResultaat)
        })
        oefeningen.moeilijskteOefeningen.sort((a, b) => {
            return preferredOrder.indexOf(a.momentoResultaat) - preferredOrder.indexOf(b.momentoResultaat)
        })
        return oefeningen
    },
    getResultsToetsen(store) {
        const toetsen = store.resultsToetsen
        toetsen.categorieen.sort((a: any, b: any) => {
            return preferredOrder.indexOf(a.momentoResultaat) - preferredOrder.indexOf(b.momentoResultaat)
        })
        toetsen.leerlingen.sort((a: any, b: any) => {
            return preferredOrder.indexOf(a.momentoResultaat) - preferredOrder.indexOf(b.momentoResultaat)
        })
        return toetsen
    },
    getResultsLoading(store) {
        return () => ({
            loading: store.loading,
            oefenenLoading: store.oefenenLoading,
            toetsenLoading: store.toetsenLoading,
            volgToetsenLoading: store.volgToetsenLoading,
            sociaalEmotioneelLoading: store.sociaalEmotioneelLoading,
            competentiesLoading: store.competentiesLoading,
            externalProviderLoading: store.externalProviderLoading,
        })
    },
    getExternalProviderUrls(store) {
        return store.externalProviderUrls
    },
    getCategories(store) {
        return () => store.categories
    },
    getCategoryOptions(store): () => {
        id: number
        naam: string
        disabled: boolean
    }[] {
        const categories = store.categories
        const categoriesWithResults = [...store.resultsToetsen.categorieen, ...store.resultsOefeningen.oefeningenResultaat.categorieen]
        const categoryOptions = categories
            .map((cat) => {
                return {
                    id: cat.labelId,
                    naam: cat.naam,
                    disabled: categoriesWithResults.filter((cat) => cat.momentoResultaat !== MomentoScore.Unknown).findIndex((catres) => catres.categorieId === cat.labelId) === -1,
                }
            })
            .sort((a, b) => {
                if (a.naam < b.naam) {
                    return -1
                }
                if (a.naam > b.naam) {
                    return 1
                }
                return 0
            })
        return () => categoryOptions
    },
    getSummarizedNoteContent(store) {
        return () => store.summarizedNoteContent
    },
    getSummarizeLoading(store) {
        return () => store.summarizeLoading
    },
}

const mutations: MutationTree<ResultsState> = {
    setFilters(state, payload: any) {
        state.filters = { ...state.filters, ...payload }
    },
    setResultsLoading(state, payload: boolean) {
        state.loading = payload
    },
    setResults(state, payload: IResult[]) {
        state.results = payload
    },
    setResultsByType(state, payload: IResultCollectionResponseLike) {
        switch (payload.resultType) {
            case ResultType.OEFENINGEN:
                payload.data.oefeningenResultaat.categorieen = payload.data.oefeningenResultaat.categorieen.sort((a, b) => a.naam.localeCompare(b.naam))
                payload.data.moeilijskteOefeningen = payload.data.moeilijskteOefeningen.sort((a, b) => a.toetsName.localeCompare(b.toetsName))
                state.resultsOefeningen = payload.data
                break
            case ResultType.TOETSEN:
                payload.data.categorieen = payload.data.categorieen.sort((a, b) => a.naam.localeCompare(b.naam))
                state.resultsToetsen = payload.data
                break
            default:
                break
        }
    },
    setCategories(state, payload: ILabel[]) {
        state.categories = payload
    },
    setSummarizedNoteContent(state, payload: string | null) {
        state.summarizedNoteContent = payload
    },
    setSummarizeLoading(state, payload: boolean) {
        state.summarizeLoading = payload
    },
    setExternalProviderUrls(state, payload: IExternalProviderUrl[]) {
        state.externalProviderUrls = payload
    },
    setExternalProviderLoading(state, payload: boolean) {
        state.externalProviderLoading = payload
    },
}

const actions: ActionTree<ResultsState, any> = {
    async getOefeningenResultsOverzicht({ commit, state }) {
        state.oefenenLoading = true

        try {
            const data = await oefeningenResultService.getOverzicht(state.filters.TimespanFilter)
            commit('setResultsByType', { resultType: ResultType.OEFENINGEN, data: data.data })
            return { resultType: ResultType.OEFENINGEN, data: data.data } as IOefeningenResultCollectionResponse
        } catch (error: any) {
            showVueNotifyError(ResultType.OEFENINGEN, error.message)
            throw error
        } finally {
            state.oefenenLoading = false
        }
    },
    async getMethodeToetsenResultsOverzicht({ commit, state }) {
        state.toetsenLoading = true

        try {
            const data = await methodeToetsResultService.getOverzicht(state.filters.ToetsTimespanFilter)
            commit('setResultsByType', { resultType: ResultType.TOETSEN, data: data.data })
            return { resultType: ResultType.TOETSEN, data: data.data } as IToetsenResultCollectionResponse
        } catch (error: any) {
            showVueNotifyError(ResultType.TOETSEN, error.message)
            throw error
        } finally {
            state.toetsenLoading = false
        }
    },
    getAllResultsOverzicht({ dispatch }) {
        dispatch('getOefeningenResultsOverzicht')
        dispatch('getMethodeToetsenResultsOverzicht')
        dispatch('getExternalProviderUrls')
    },
    async fetchCategories({ commit }) {
        try {
            const response = await labelsService.getLabelsByType(LabelType.Vakgebied)
            commit('setCategories', response.data)
            return response.data
        } catch (error: any) {
            console.error('Error fetching categories:', error)
            throw error
        }
    },
    async summarizeResultToNotitie({ commit, state }, resultaatId: number) {
        state.summarizeLoading = true
        commit('setSummarizedNoteContent', null)

        try {
            const response = await summarizeToNotitieService.summarizeResult(resultaatId)
            if (response.data.success) {
                commit('setSummarizedNoteContent', response.data.content)
                return response.data.content
            } else {
                throw new Error(response.data.message || 'Failed to summarize result')
            }
        } catch (error: any) {
            console.error('Store action: Error in summarizeResultToNotitie:', error)
            notify({
                group: 'requests',
                title: 'Foutmelding',
                type: 'error',
                text: `Er ging iets mis met het samenvatten van het resultaat: ${error.message}`,
            })
            throw error
        } finally {
            state.summarizeLoading = false
        }
    },
    async getExternalProviderUrls({ commit, state }) {
        commit('setExternalProviderLoading', true)

        try {
            const urls = await externalProviderUrlService.getCombinedUrls(state.filters.VolgenTimespanFilter)
            commit('setExternalProviderUrls', urls)
            return urls
        } catch (error: any) {
            console.error('Error fetching external provider URLs:', error)
            commit('setExternalProviderUrls', [])
            notify({
                group: 'requests',
                title: 'Foutmelding',
                type: 'error',
                text: `Er ging iets mis met het ophalen van externe leverancier URLs: ${error.message}`,
            })
            throw error
        } finally {
            commit('setExternalProviderLoading', false)
        }
    },
}

export default {
    state: new ResultsState(),
    getters: getters,
    mutations: mutations,
    actions: actions,
}
